import { publishingTerms, publishingTerm } from "~/core/channel-directory";
import { Button, ChipInput, ChipSelect, Popover, PopoverContent, PopoverTrigger } from "@ondit/ui";
import { Search } from "@ondit/ui/icons";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAccess } from "~/core/access/index";
import type { PublishingPlatform } from "~/core/api/distribution";
import { api } from "~/core/api/index";
import * as fmt from "~/core/format";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { PlatformIdentity } from "~/modules/analytics/components/platform-identity";
import { ConnectAccount } from "./connect";

/** The server's `CATEGORIES`, in the order the filter lists them. */
export const CATEGORIES = [
	"search",
	"launch",
	"directory",
	"ai",
	"startup",
	"social",
	"video",
	"blog",
	"qa",
	"community",
	"developer",
	"media",
	"navigation",
	"design",
	"local",
	"review",
	"industry",
	"b2b",
	"wiki",
] as const;

/** The server's `INDUSTRIES`, in the order the filter lists them. */
export const INDUSTRIES = [
	"legal",
	"healthcare",
	"dental",
	"wellness",
	"home_services",
	"real_estate",
	"automotive",
	"restaurants",
	"travel",
	"events",
	"pets",
	"education",
	"agencies",
	"software",
	"manufacturing",
	"ecommerce",
	"finance",
	"recruiting",
	"nonprofit",
	"startups",
	"ai",
	"developers",
	"design",
	"apps",
] as const;

export const TIERS = ["high", "medium", "low"] as const;

/** What a brand can file its own platform under; the filter reads the data instead. */
const LANGUAGES = ["en", "zh", "ja", "ko", "es", "fr", "de", "pt", "ru", "ar", "it", "id", "vi", "th"];

export interface PlatformFilter {
	languages?: string[];
	categories?: string[];
	industries?: string[];
	tiers?: string[];
}

/**
 * A platform with no languages takes any, and one with no industries serves
 * any, so each answers every filter of its kind — Google Business Profile is
 * as much a dentist's as a bakery's.
 */
export function matches(
	p: PublishingPlatform,
	{ languages = [], categories = [], industries = [], tiers = [] }: PlatformFilter,
) {
	const langs = p.languages ?? [];
	const serves = p.industries ?? [];
	return (
		(languages.length === 0 || langs.length === 0 || langs.some((l) => languages.includes(l))) &&
		(categories.length === 0 || (p.categories ?? []).some((c) => categories.includes(c))) &&
		(industries.length === 0 || serves.length === 0 || serves.some((i) => industries.includes(i))) &&
		(tiers.length === 0 || (!!p.value_tier && tiers.includes(p.value_tier)))
	);
}

/**
 * Most valuable first. The list is six hundred platforms long, and in the
 * bundled order a search console sat below forty AI-tool directories.
 */
export function byValue(a: PublishingPlatform, b: PublishingPlatform) {
	return (b.value_score ?? -1) - (a.value_score ?? -1);
}

export function PublishingPlatforms({ entityId }: { entityId: string }) {
	const t = useT();
	const { locale } = useI18n();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "distribution"],
		queryFn: () => api.distribution(entityId),
		...read,
	});
	const accounts = useQuery({
		queryKey: [...keys.entity(entityId), "platform-tasks"],
		queryFn: () => api.platformTasks(entityId),
	});
	const [search, setSearch] = useState("");
	const [languages, setLanguages] = useState<string[]>([]);
	const [categories, setCategories] = useState<string[]>([]);
	const [industries, setIndustries] = useState<string[]>([]);
	const [tiers, setTiers] = useState<string[]>([]);
	return (
		<Async state={state}>
			{(data) => {
				const shown = data.platforms
					.filter(
						(p) =>
							`${p.name} ${p.domain}`.toLowerCase().includes(search.toLowerCase()) &&
							matches(p, { languages, categories, industries, tiers }),
					)
					.sort(byValue);
				const present = [...new Set(data.platforms.flatMap((p) => p.languages ?? []))].sort();
				return (
					<Section
						title={t("publishing.heading")}
						lede={t("distribution.rulesHint")}
						action={<span className="hint">{t("publishFlow.distributionHelp")}</span>}
					>
						<div className="publishing-settings">
							<div className="publishing-toolbar">
								<div className="ml-auto flex flex-wrap items-center gap-(--s-2)">
									<ChipSelect
										multiSelect
										showAllOption
										allOptionLabel={t("publishing.category")}
										multiSelectValues={categories}
										onMultiSelectChange={setCategories}
										aria-label={t("publishing.category")}
										options={publishingTerms("categories").map((term) => ({
											value: term.id,
											label: pick(locale, term.label),
										}))}
									/>
									<ChipSelect
										multiSelect
										showAllOption
										allOptionLabel={t("publishing.industry")}
										multiSelectValues={industries}
										onMultiSelectChange={setIndustries}
										aria-label={t("publishing.industry")}
										options={publishingTerms("industries").map((term) => ({
											value: term.id,
											label: pick(locale, term.label),
										}))}
									/>
									<ChipSelect
										multiSelect
										showAllOption
										allOptionLabel={t("publishing.value")}
										multiSelectValues={tiers}
										onMultiSelectChange={setTiers}
										aria-label={t("publishing.value")}
										options={publishingTerms("tiers").map((term) => ({
											value: term.id,
											label: pick(locale, term.label),
										}))}
									/>
									<ChipSelect
										multiSelect
										showAllOption
										allOptionLabel={t("publishing.language")}
										multiSelectValues={languages}
										onMultiSelectChange={setLanguages}
										aria-label={t("publishing.language")}
										options={present.map((l) => ({ value: l, label: fmt.language(locale, l) }))}
									/>
									<ChipInput
										className="w-[calc(var(--s-14)*3.5)] max-w-full"
										icon={Search}
										value={search}
										onChange={(e) => setSearch(e.target.value)}
										placeholder={t("distribution.search")}
										aria-label={t("distribution.search")}
									/>
								</div>
							</div>
							<ul className="publishing-platforms">
								{shown.map((p) => (
									<PlatformRow key={p.id} platform={p}>
										<Account
											entityId={entityId}
											platform={p}
											accounts={accounts.data?.accounts}
											managedAvailable={accounts.data?.account_capabilities?.some(
												(c) => c.platform_id === p.id && c.managed_available,
											)}
										/>
									</PlatformRow>
								))}
							</ul>
						</div>
					</Section>
				);
			}}
		</Async>
	);
}

const FLAGS = ["images", "video", "links"] as const;

/**
 * The tier, and the score and traffic rank behind it, with what the number
 * does not mean on hover. In a table column the tier stands alone and the
 * figures sit under it, or the column took a third of the row.
 */
export function PlatformValue({ platform: p, stacked = false }: { platform: PublishingPlatform; stacked?: boolean }) {
	const t = useT();
	const { locale } = useI18n();
	if (!p.value_tier || p.value_score == null) return null;
	const tier = t(`publishing.value.${p.value_tier}`);
	const figures = [
		t("publishing.value.score", { score: String(p.value_score) }),
		p.traffic_rank ? t("publishing.rank", { rank: fmt.number(locale, p.traffic_rank) }) : null,
	]
		.filter(Boolean)
		.join(" · ");
	return stacked ? (
		<span title={t("publishing.value.hint")}>
			{tier}
			<span className="faint block">{figures}</span>
		</span>
	) : (
		<li title={t("publishing.value.hint")}>
			{tier} · {figures}
		</li>
	);
}

/**
 * One platform: who, what it takes, what the rules say, and where they came
 * from. The three rules are marks rather than a label over a value: most rows
 * answer "check with platform" for all three, and four columns of that made
 * forty-four platforms eight screens of the same word.
 */
export function PlatformRow({ platform: p, children }: { platform: PublishingPlatform; children: React.ReactNode }) {
	const t = useT();
	const { locale } = useI18n();
	const unknown = FLAGS.every((flag) => p[flag] === "check");
	return (
		<li className="publishing-platform">
			<div className="publishing-platform-head">
				<a href={p.url} target="_blank" rel="noopener noreferrer">
					<PlatformIdentity platform={p} />
				</a>
				<span className="hint">
					{[
						p.domain,
						...(p.categories ?? []).map((c) => publishingTerm("categories", c, locale)),
						p.languages?.length
							? p.languages.map((l) => fmt.language(locale, l)).join(" / ")
							: t("publishing.language.any"),
						p.countries?.length ? p.countries.map((c) => fmt.region(locale, c)).join(" / ") : null,
					]
						.filter(Boolean)
						.join(" · ")}
				</span>
				<div className="publishing-platform-action">{children}</div>
			</div>
			{p.summary && <p className="publishing-platform-summary">{pick(locale, p.summary)}</p>}
			{p.fit && <p className="publishing-platform-notes">{t("publishing.fit", { fit: pick(locale, p.fit) })}</p>}
			<ul className="publishing-platform-facts">
				<PlatformValue platform={p} />
				<li>{t(`channels.cadence.${p.cadence ?? "ongoing"}`)}</li>
				<li>{t(`distribution.${p.format}`)}</li>
				{p.cost && p.cost !== "check" && <li>{t(`publishing.cost.${p.cost}`)}</li>}
				{p.link_type && p.link_type !== "check" && <li>{t(`publishing.link.${p.link_type}`)}</li>}
				{p.industries?.length ? (
					<li>{p.industries.map((i) => publishingTerm("industries", i, locale)).join(" / ")}</li>
				) : null}
				{unknown ? (
					<li data-state="check">{t("publishing.rulesUnknown")}</li>
				) : (
					FLAGS.map((flag) => (
						<li key={flag} data-state={p[flag]}>
							{t(`distribution.${flag}`)} · {t(`distribution.${p[flag]}`)}
						</li>
					))
				)}
			</ul>
			{p.notes && <p className="publishing-platform-notes">{pick(locale, p.notes)}</p>}
			<div className="publishing-platform-foot">
				{(p.reviewed_at || p.verified_at) && (
					<span>{t("distribution.reviewed", { date: p.reviewed_at ?? p.verified_at ?? "" })}</span>
				)}
				<Popover>
					<PopoverTrigger asChild>
						<Button variant="link" className="h-auto p-0 text-[length:inherit]">
							{t("distribution.sources")}
							<span className="sr-only"> · {p.name}</span>
						</Button>
					</PopoverTrigger>
					<PopoverContent className="platform-sources">
						<strong>
							{p.name} · {t("distribution.sources")}
						</strong>
						{(p.sources?.length ? p.sources : [p.rules_url]).map((url, index) => (
							<a key={url} href={url} target="_blank" rel="noopener noreferrer">
								{index + 1}. {new URL(url).hostname}
							</a>
						))}
						{p.logo_source_url && (
							<a href={p.logo_source_url} target="_blank" rel="noopener noreferrer">
								{t("distribution.logoSource")}
							</a>
						)}
					</PopoverContent>
				</Popover>
			</div>
		</li>
	);
}

function Account({
	entityId,
	platform,
	accounts = [],
	managedAvailable = false,
}: {
	entityId: string;
	platform: PublishingPlatform;
	managedAvailable?: boolean;
	accounts?: { id: string; provider_id: string; name: string; status?: string }[];
}) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	// Account connection follows the complete service catalog, independently
	// of whether the channel has an executable publication contract yet.
	const contract = {
		id: platform.id,
		name: platform.name,
		credential_provider: `pubzu:${platform.id}`,
		credential_kind: "pubzu" as const,
	};
	const mine = accounts.filter((a) => a.provider_id === contract.credential_provider);
	return (
		<div className="row-actions">
			{managedAvailable && (
				<span className="hint">{pick(locale, ["Platform account available", "可使用平台账号"])}</span>
			)}
			{mine.map((a) => (
				<span key={a.id} className="row-actions">
					<span className="hint">
						{a.name} ·{" "}
						{a.status === "connected"
							? pick(locale, ["Connected", "已连接"])
							: pick(locale, ["Needs connection", "待连接"])}
					</span>
					<ConnectAccount
						entityId={entityId}
						platform={contract}
						accountId={a.id}
						size="sm"
						variant="outline"
						disabled={!editor}
					/>
				</span>
			))}
			<ConnectAccount entityId={entityId} platform={contract} size="sm" variant="outline" disabled={!editor} />
		</div>
	);
}
